import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import CallRoundedIcon from "@mui/icons-material/CallRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ToggleOffRoundedIcon from "@mui/icons-material/ToggleOffRounded";
import ToggleOnRoundedIcon from "@mui/icons-material/ToggleOnRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import StorefrontRoundedIcon from "@mui/icons-material/StorefrontRounded";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import { Link as RouterLink } from "react-router-dom";
import {
  ACTIVE_STATUS,
  buildExtendedTrialDate,
  formatDateTime,
  getFullAddress,
  getMapHref,
  getPhoneHref,
  getPrimaryEmail,
  getPrimaryPhone,
  hasTrialOverride,
  INACTIVE_STATUS,
  isBusinessActive,
  isCareDirectoryApproved,
  isCareDirectoryVisible,
  isBusinessClaimed,
  normalizeText,
  TRIAL_OVERRIDE_PLAN_ID,
} from "./shared";
import { PayoutSummary, SubscriptionSummary } from "./BillingSummaryCards";

type Props = {
  copyText: (value: string, label: string) => void;
  runBusinessClaimRelease: (business: any) => void;
  runCareDirectoryApproval: (business: any) => void;
  runBusinessUpdate: (business: any, patch: Record<string, any>, successText: string) => void;
  savingAction: string;
  selectedBusiness: any;
};

export function BusinessDetailsPanel({
  copyText,
  runBusinessClaimRelease,
  runCareDirectoryApproval,
  runBusinessUpdate,
  savingAction,
  selectedBusiness,
}: Props) {
  if (!selectedBusiness) {
    return (
      <Paper variant="outlined" sx={{ flex: 1, borderRadius: 4, p: { xs: 2, sm: 3 } }}>
        <Alert severity="info">Select a business to manage its admin controls.</Alert>
      </Paper>
    );
  }

  const primaryEmail = getPrimaryEmail(selectedBusiness);
  const primaryPhone = getPrimaryPhone(selectedBusiness);
  const businessUrl = normalizeText(selectedBusiness?.url);
  const fullAddress = getFullAddress(selectedBusiness);
  const selectedBusinessMapHref = getMapHref(selectedBusiness);
  const businessPageHref = businessUrl ? `/b/${businessUrl}` : "";
  const careDirectoryApproved = isCareDirectoryApproved(selectedBusiness);
  const careDirectoryVisible = isCareDirectoryVisible(selectedBusiness);
  const careApprovalInProgress =
    savingAction === "Approved care listing and enabled directory visibility.";
  const handleCareDirectoryAction = () => {
    if (!careDirectoryApproved) {
      runCareDirectoryApproval(selectedBusiness);
      return;
    }

    runBusinessUpdate(
      selectedBusiness,
      {
        displayOnBookingPage: !selectedBusiness?.displayOnBookingPage,
      },
      selectedBusiness?.displayOnBookingPage
        ? "Hidden from Plany365 Care directory."
        : "Shown on Plany365 Care directory.",
    );
  };

  return (
    <Paper variant="outlined" sx={{ flex: 1, borderRadius: 4, p: { xs: 2, sm: 3 } }}>
      <Stack spacing={2.2}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="overline" color="primary.main">
              Business Overview
            </Typography>
            <Typography variant="h5" fontWeight={800}>
              {selectedBusiness?.name || "Unnamed business"}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}
            >
              {fullAddress || "No address yet"}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              size="small"
              variant="outlined"
              color={isBusinessActive(selectedBusiness) ? "success" : "default"}
              label={isBusinessActive(selectedBusiness) ? "Active" : "Inactive"}
            />
            <Chip
              size="small"
              variant="outlined"
              color={isBusinessClaimed(selectedBusiness) ? "success" : "warning"}
              label={isBusinessClaimed(selectedBusiness) ? "Claimed" : "Unclaimed"}
            />
            <Chip
              size="small"
              variant="outlined"
              color={careDirectoryVisible ? "success" : "default"}
              label={`Care directory: ${careDirectoryVisible ? "Visible" : "Hidden"}`}
            />
          </Stack>
        </Stack>

        <Divider />

        <Stack direction={{ xs: "column", xl: "row" }} spacing={2}>
          <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <StorefrontRoundedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Quick Contact
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Use these for quick outreach or handoff when you need to talk with the business
                  directly.
                </Typography>
                <Stack spacing={1}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" sx={{ minWidth: 0, flex: 1 }}>
                      <strong>Email:</strong> {primaryEmail || "No email"}
                    </Typography>
                    <Stack direction="row" spacing={0.25} flexShrink={0}>
                      <IconButton
                        size="small"
                        component="a"
                        href={primaryEmail ? `mailto:${primaryEmail}` : undefined}
                        disabled={!primaryEmail}
                      >
                        <MailOutlineRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => copyText(primaryEmail, "Email")}
                        disabled={!primaryEmail}
                      >
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" sx={{ minWidth: 0, flex: 1 }}>
                      <strong>Phone:</strong> {primaryPhone || "No phone"}
                    </Typography>
                    <Stack direction="row" spacing={0.25} flexShrink={0}>
                      <IconButton
                        size="small"
                        component="a"
                        href={primaryPhone ? getPhoneHref(primaryPhone) : undefined}
                        disabled={!primaryPhone}
                      >
                        <CallRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => copyText(primaryPhone, "Phone")}
                        disabled={!primaryPhone}
                      >
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" sx={{ minWidth: 0, flex: 1 }}>
                      <strong>Address:</strong> Google Map
                    </Typography>
                    <Stack direction="row" spacing={0.25} flexShrink={0}>
                      <IconButton
                        size="small"
                        component="a"
                        href={selectedBusinessMapHref || undefined}
                        target="_blank"
                        rel="noreferrer"
                        disabled={!selectedBusinessMapHref}
                      >
                        <PlaceOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => copyText(fullAddress, "Address")}
                        disabled={!fullAddress}
                      >
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                  <Typography variant="body2">
                    <strong>AFH License:</strong>{" "}
                    {selectedBusiness?.careSettings?.afhLicenseNumber || "Not recorded"}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography variant="body2" sx={{ minWidth: 0, flex: 1 }}>
                      <strong>Public Page:</strong> {businessUrl ? "Open listing" : "Not set"}
                    </Typography>
                    <Stack direction="row" spacing={0.25} flexShrink={0}>
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={businessPageHref || "/"}
                        disabled={!businessUrl}
                        target="_blank"
                      >
                        <OpenInNewRoundedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => copyText(businessUrl, "URL")}
                        disabled={!businessUrl}
                      >
                        <ContentCopyRoundedIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </Stack>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PaymentsRoundedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Billing And Payout
                  </Typography>
                </Stack>
                <SubscriptionSummary businessId={Number(selectedBusiness?.id || 0)} />
                <PayoutSummary businessId={Number(selectedBusiness?.id || 0)} />
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Stack direction={{ xs: "column", xl: "row" }} spacing={2}>
          <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LinkOffRoundedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Claim Ownership
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Use this when a business was claimed by the wrong account. It returns the
                  business to unclaimed and removes that claimant&apos;s direct business access.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    variant="outlined"
                    color={isBusinessClaimed(selectedBusiness) ? "success" : "default"}
                    label={isBusinessClaimed(selectedBusiness) ? "Currently claimed" : "Currently unclaimed"}
                  />
                  {selectedBusiness?.createdBy ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Claimed by user #${selectedBusiness.createdBy}`}
                    />
                  ) : null}
                </Stack>
                {isBusinessClaimed(selectedBusiness) ? (
                  <Button
                    size="small"
                    color="warning"
                    variant="outlined"
                    startIcon={<LinkOffRoundedIcon />}
                    disabled={Boolean(savingAction)}
                    onClick={() => {
                      if (
                        window.confirm(
                          "Revoke this business claim and return it to unclaimed status?",
                        )
                      ) {
                        runBusinessClaimRelease(selectedBusiness);
                      }
                    }}
                  >
                    Revoke Claim
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    Nothing to revoke right now. This business is already unclaimed.
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <VerifiedRoundedIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Care Directory Approval
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  Approve the care listing here when the business is valid. This removes the
                  pending care approval state. Once approved, you can show or hide the business on
                  the Care directory without re-approving it.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    variant="outlined"
                    color={careDirectoryApproved ? "success" : "warning"}
                    label={
                      careDirectoryApproved
                        ? "Care approval completed"
                        : "Pending care approval"
                    }
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    color={selectedBusiness?.displayOnBookingPage ? "success" : "default"}
                    label={
                      selectedBusiness?.displayOnBookingPage
                        ? "Directory toggle on"
                        : "Directory toggle off"
                    }
                  />
                </Stack>
                {selectedBusiness?.careSettings?.verifiedAt ? (
                  <Typography variant="caption" color="text.secondary">
                    Verified {formatDateTime(selectedBusiness.careSettings.verifiedAt)}
                  </Typography>
                ) : null}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<VerifiedRoundedIcon />}
                  disabled={Boolean(savingAction)}
                  onClick={handleCareDirectoryAction}
                >
                  {!careDirectoryApproved
                    ? "Approve And Show On Care Directory"
                    : selectedBusiness?.displayOnBookingPage
                      ? "Hide From Care Directory"
                      : "Show On Care Directory"}
                </Button>
                {!isBusinessActive(selectedBusiness) ? (
                  <Typography variant="caption" color="text.secondary">
                    This only handles care approval and listing visibility. Business active status
                    is controlled separately below.
                  </Typography>
                ) : null}
                {careApprovalInProgress ? (
                  <Typography variant="caption" color="text.secondary">
                    Saving care approval and visibility...
                  </Typography>
                ) : null}
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={1.25}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Trial Override
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Extend the business-level premium trial without changing the billing
                  subscription.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    variant="outlined"
                    color={hasTrialOverride(selectedBusiness) ? "info" : "default"}
                    label={
                      hasTrialOverride(selectedBusiness)
                        ? `Trial until ${formatDateTime(selectedBusiness?.exemptedPlanExpiresAt)}`
                        : "No active trial override"
                    }
                  />
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EventAvailableRoundedIcon />}
                    disabled={Boolean(savingAction)}
                    onClick={() =>
                      runBusinessUpdate(
                        selectedBusiness,
                        {
                          exemptedPlanId: TRIAL_OVERRIDE_PLAN_ID,
                          exemptedPlanExpiresAt: buildExtendedTrialDate(
                            selectedBusiness?.exemptedPlanExpiresAt,
                            14,
                          ),
                        },
                        "Extended trial by 14 days.",
                      )
                    }
                  >
                    Extend 14 Days
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EventAvailableRoundedIcon />}
                    disabled={Boolean(savingAction)}
                    onClick={() =>
                      runBusinessUpdate(
                        selectedBusiness,
                        {
                          exemptedPlanId: TRIAL_OVERRIDE_PLAN_ID,
                          exemptedPlanExpiresAt: buildExtendedTrialDate(
                            selectedBusiness?.exemptedPlanExpiresAt,
                            30,
                          ),
                        },
                        "Extended trial by 30 days.",
                      )
                    }
                  >
                    Extend 30 Days
                  </Button>
                  {hasTrialOverride(selectedBusiness) ? (
                    <Button
                      size="small"
                      color="warning"
                      variant="outlined"
                      startIcon={<EventBusyRoundedIcon />}
                      disabled={Boolean(savingAction)}
                      onClick={() =>
                        runBusinessUpdate(
                          selectedBusiness,
                          {
                            exemptedPlanId: null,
                            exemptedPlanExpiresAt: null,
                          },
                          "Cleared the business trial override.",
                        )
                      }
                    >
                      Clear Override
                    </Button>
                  ) : null}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined" sx={{ flex: 1, borderRadius: 3 }}>
            <CardContent>
              <Stack spacing={1.25}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Business Status
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Use this when Plany365 needs to pause or restore the business in active platform
                  flows.
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    variant="outlined"
                    color={isBusinessActive(selectedBusiness) ? "success" : "default"}
                    label={
                      isBusinessActive(selectedBusiness)
                        ? "Business is active"
                        : "Business is inactive"
                    }
                  />
                </Stack>
                <Button
                  size="small"
                  variant="contained"
                  color={isBusinessActive(selectedBusiness) ? "warning" : "success"}
                  startIcon={
                    isBusinessActive(selectedBusiness) ? (
                      <ToggleOffRoundedIcon />
                    ) : (
                      <ToggleOnRoundedIcon />
                    )
                  }
                  disabled={Boolean(savingAction)}
                  onClick={() =>
                    runBusinessUpdate(
                      selectedBusiness,
                      {
                        status: isBusinessActive(selectedBusiness)
                          ? INACTIVE_STATUS
                          : ACTIVE_STATUS,
                      },
                      isBusinessActive(selectedBusiness)
                        ? "Marked business inactive."
                        : "Marked business active.",
                    )
                  }
                >
                  {isBusinessActive(selectedBusiness) ? "Make Inactive" : "Make Active"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Stack>
    </Paper>
  );
}
